import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Radio } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useTrackerProtocols,
  useCreateTrackerProtocol,
  useUpdateTrackerProtocolLabel,
  useDeleteTrackerProtocol,
} from "@/hooks/queries/platform";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import type { TrackerProtocolEntry } from "@/services/gpsdrive-tracker-protocols.service";

export const Route = createFileRoute("/master/tracker-protocols")({
  component: () => (
    <MasterShell>
      <TrackerProtocolsPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Protocolos — Super Master — GPS Drive" }] }),
});

function TrackerProtocolsPage() {
  const { data: protocols, isLoading } = useTrackerProtocols();

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Protocolos</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo de protocolos de rastreador disponíveis pra qualquer empresa escolher no
            cadastro de veículo e no catálogo de Comandos GT06. Cadastre aqui se alguma empresa
            usar um protocolo além dos já existentes.
          </p>
        </div>
        <TrackerProtocolFormDialog
          trigger={
            <Button>
              <Plus className="h-4 w-4" />
              Novo protocolo
            </Button>
          }
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !protocols || protocols.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Radio className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum protocolo cadastrado ainda</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome de exibição</TableHead>
                <TableHead>Identificador</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {protocols.map((protocol) => (
                <ProtocolRow key={protocol.id} protocol={protocol} />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function ProtocolRow({ protocol }: { protocol: TrackerProtocolEntry }) {
  const deleteProtocol = useDeleteTrackerProtocol();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteProtocol.mutateAsync(protocol.id);
      toast.success("Protocolo excluído");
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao excluir o protocolo");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{protocol.label}</TableCell>
      <TableCell className="font-mono text-muted-foreground">{protocol.value}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <TrackerProtocolFormDialog
            protocol={protocol}
            trigger={
              <Button variant="outline" size="sm">
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Button>
            }
          />
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir "{protocol.label}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  Some da lista de opções pra novos cadastros — veículos e comandos que já usam
                  este protocolo continuam com o valor salvo, sem mudança.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={deleteProtocol.isPending}>
                  {deleteProtocol.isPending ? "Excluindo…" : "Excluir"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}

function TrackerProtocolFormDialog({
  protocol,
  trigger,
}: {
  protocol?: TrackerProtocolEntry;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const createProtocol = useCreateTrackerProtocol();
  const updateLabel = useUpdateTrackerProtocolLabel();
  const isEditing = Boolean(protocol);
  const isPending = createProtocol.isPending || updateLabel.isPending;

  useEffect(() => {
    if (!open) return;
    setValue(protocol?.value ?? "");
    setLabel(protocol?.label ?? "");
  }, [open, protocol]);

  const handleSave = async () => {
    if (!label.trim()) {
      toast.error("Informe o nome de exibição");
      return;
    }
    try {
      if (protocol) {
        await updateLabel.mutateAsync({ id: protocol.id, label: label.trim() });
        toast.success("Protocolo atualizado");
      } else {
        if (!value.trim()) {
          toast.error("Informe o identificador do protocolo");
          return;
        }
        await createProtocol.mutateAsync({ value: value.trim().toLowerCase(), label: label.trim() });
        toast.success("Protocolo criado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao salvar o protocolo");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar protocolo" : "Novo protocolo"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "O identificador não pode ser alterado depois de criado — só o nome de exibição."
              : "O identificador é a chave técnica salva no cadastro (ex.: meitrack) — use só letras minúsculas, números e hífen."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="protocol-value">Identificador</Label>
              <Input
                id="protocol-value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Ex: meitrack"
                className="font-mono"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="protocol-label">Nome de exibição</Label>
            <Input
              id="protocol-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Meitrack"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
